import { InfoCircleTwoTone } from "@ant-design/icons";
import type { FormInstance, FormListFieldData } from "antd";
import { Form, Select, Tooltip } from "antd";
import { SURVIVORSHIP_RULES } from "common/enums";
import React from "react";
import { Segment } from "../Segment";

interface SchemaSurvivorshipRuleFieldPropertyProps {
    formRef: React.RefObject<FormInstance>;
    selectedFieldRow: FormListFieldData;
    readOnly: boolean;
}

const RULE_DESCRIPTIONS: Record<string, string> = {
    [SURVIVORSHIP_RULES.UPDATE_IF_SOURCE_NOT_NULL]: "Updates existing records with new data, but leaves current data intact if the incoming value is blank.",
    [SURVIVORSHIP_RULES.ALWAYS_SOURCE]: "Incoming data will always overwrite the existing value, even if the new data is blank.",
    [SURVIVORSHIP_RULES.RETAIN_LOCAL]: "Protects the current value. Only fills it during an import if it is currently blank.",
    [SURVIVORSHIP_RULES.FILL_LOCAL_BLANKS]: "Functions like an insert-only merge. Existing data is never overwritten.",
    [SURVIVORSHIP_RULES.SMART_MERGE_PREFER_LOCAL]:
        "Imports update this field normally, but if a human manually edits this in the Dataview, future imports won't overwrite their change.",
    [SURVIVORSHIP_RULES.SMART_MERGE_PREFER_SOURCE]: "Normal update behavior, but also intentionally resets any human-edit tracking for this field.",
};

const SchemaSurvivorshipRuleFieldProperty: React.FC<SchemaSurvivorshipRuleFieldPropertyProps> = ({ formRef, selectedFieldRow, readOnly }) => {
    const form = Form.useFormInstance();
    const activeForm = form || formRef.current || undefined;

    const fields = Form.useWatch("fields", activeForm);
    const hasPrimaryKey = Array.isArray(fields) && fields.some((f: any) => f?.isPrimaryKey);
    const currentField = fields?.[selectedFieldRow.name];
    const isCurrentFieldPrimaryKey = !!currentField?.isPrimaryKey;

    // Use current form value for the extra description (fallback to recommended if blank)
    const currentRule = Form.useWatch(["fields", selectedFieldRow.name, "survivorshipRule"], activeForm) || SURVIVORSHIP_RULES.UPDATE_IF_SOURCE_NOT_NULL;

    // If the schema has no primary key OR the current field is the primary key, hide entirely.
    // As identified, sending a survivorship rule payload for the PK itself makes no architectural sense
    // since the PK is the anchor used to perform the merge exactly.
    if (!hasPrimaryKey || isCurrentFieldPrimaryKey) {
        return null;
    }

    return (
        <Segment.Group>
            <Segment className="form-background">
                <Form.Item
                    label={
                        <span className="flex items-center gap-1">
                            Import Update Rule
                            <Tooltip title="Rule applied when a Bulk Upload/Import matches an existing record.">
                                <InfoCircleTwoTone />
                            </Tooltip>
                        </span>
                    }
                    name={["fields", selectedFieldRow.name, "survivorshipRule"]}
                    initialValue={SURVIVORSHIP_RULES.UPDATE_IF_SOURCE_NOT_NULL}
                    extra={<div className="mt-1 text-xs text-slate-500">{RULE_DESCRIPTIONS[currentRule]}</div>}
                >
                    <Select disabled={readOnly} placeholder="Select a survivorship rule" dropdownMatchSelectWidth={false}>
                        <Select.Option value={SURVIVORSHIP_RULES.UPDATE_IF_SOURCE_NOT_NULL}>Update if Source is not Blank (Recommended)</Select.Option>
                        <Select.Option value={SURVIVORSHIP_RULES.ALWAYS_SOURCE}>Always Overwrite from Source</Select.Option>
                        <Select.Option value={SURVIVORSHIP_RULES.RETAIN_LOCAL}>Protect Initial Local Value</Select.Option>
                        <Select.Option value={SURVIVORSHIP_RULES.FILL_LOCAL_BLANKS}>Only Fill Blank Local Values</Select.Option>
                        <Select.Option value={SURVIVORSHIP_RULES.SMART_MERGE_PREFER_LOCAL}>Smart Merge: Protect Human Edits</Select.Option>
                        <Select.Option value={SURVIVORSHIP_RULES.SMART_MERGE_PREFER_SOURCE}>Smart Merge: Force Source Updates</Select.Option>
                    </Select>
                </Form.Item>
            </Segment>
        </Segment.Group>
    );
};

export default SchemaSurvivorshipRuleFieldProperty;
